import React from 'react';
import clsx from 'clsx';

interface ShipLoadingProps {
  size?: "sm" | "md" | "lg";
  showProgress?: boolean;
  progress?: number; // 0 - 100
  label?: string;
  className?: string;
  fullScreen?: boolean; // Default to true to keep backward compatibility
}

const sizeMap = {
  sm: { wrapper: "h-24 w-24", ship: 48, wave: 80 },
  md: { wrapper: "h-40 w-40", ship: 72, wave: 120 },
  lg: { wrapper: "h-56 w-56", ship: 96, wave: 160 },
};

export default function ShipLoading({
  size = "md",
  showProgress = false,
  progress = 0,
  label = "Loading...",
  className,
  fullScreen = true,
}: ShipLoadingProps) {
  const s = sizeMap[size];
  const clamped = Math.min(100, Math.max(0, progress));

  const loaderContent = (
    <div className={clsx("flex flex-col items-center justify-center gap-4", className)}>
      <style>{`
        @keyframes ship-rock {
          0%, 100% { transform: rotate(-4deg) translateY(0px); }
          25% { transform: rotate(3deg) translateY(-3px); }
          50% { transform: rotate(-2deg) translateY(0px); }
          75% { transform: rotate(4deg) translateY(-2px); }
        }
        @keyframes ship-smoke {
          0% { opacity: 0.6; transform: translate(0, 0) scale(1); }
          100% { opacity: 0; transform: translate(6px, -14px) scale(1.4); }
        }
        @keyframes ship-wave {
          0%, 100% { transform: translateX(0px); }
          50% { transform: translateX(4px); }
        }
      `}</style>

      <div
        className={clsx("relative flex items-center justify-center", s.wrapper)}
        aria-label={label}
        role="status"
      >
        {/* Rotating outer rings */}
        <svg
          width={s.wave}
          height={s.wave}
          viewBox="0 0 160 160"
          className="absolute text-brand-500/20 animate-spin"
          style={{ animationDuration: "8s" }}
        >
          <circle cx="80" cy="80" r="70" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.3" />
          <circle cx="80" cy="80" r="55" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.2" />
        </svg>

        {/* Ping rings */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span
            className="absolute rounded-full border border-brand-500/30 animate-ping"
            style={{ width: s.wave * 0.6, height: s.wave * 0.6, animationDuration: "2s" }}
          />
          <span
            className="absolute rounded-full border border-brand-500/20 animate-ping"
            style={{ width: s.wave * 0.8, height: s.wave * 0.8, animationDuration: "2.5s", animationDelay: "0.5s" }}
          />
        </div>

        {/* Ship SVG with rocking animation */}
        <div className="relative z-10 text-brand-500" style={{ animation: "ship-rock 3s ease-in-out infinite" }}>
          <svg
            width={s.ship}
            height={s.ship}
            viewBox="0 0 72 72"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            {/* Hull */}
            <path
              d="M8 44c4 10 12 16 28 16s24-6 28-16H8z"
              fill="currentColor"
              opacity="0.9"
            />
            {/* Deck */}
            <path
              d="M12 44h40l-4-6H16l-4 6z"
              fill="currentColor"
              opacity="0.6"
            />
            {/* Cabin */}
            <rect x="24" y="26" width="14" height="12" rx="2" fill="currentColor" opacity="0.85" />
            {/* Windows */}
            <circle cx="28" cy="32" r="2" className="fill-white dark:fill-gray-900" />
            <circle cx="34" cy="32" r="2" className="fill-white dark:fill-gray-900" />
            {/* Smokestack */}
            <rect x="38" y="18" width="6" height="10" rx="1.5" fill="currentColor" />
            {/* Smoke puffs - animated via CSS */}
            <g style={{ animation: "ship-smoke 2s ease-out infinite" }}>
              <circle cx="41" cy="14" r="2.5" className="fill-gray-400 dark:fill-gray-500" opacity="0.6" />
            </g>
            <g style={{ animation: "ship-smoke 2s ease-out infinite 0.4s" }}>
              <circle cx="44" cy="10" r="2" className="fill-gray-400 dark:fill-gray-500" opacity="0.4" />
            </g>
            <g style={{ animation: "ship-smoke 2s ease-out infinite 0.8s" }}>
              <circle cx="47" cy="6" r="1.5" className="fill-gray-400 dark:fill-gray-500" opacity="0.25" />
            </g>
            {/* Water line wave */}
            <path
              d="M4 50c8-3 16 3 24 0s16-3 24 0 16 3 16 0"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              fill="none"
              opacity="0.7"
              style={{ animation: "ship-wave 2s ease-in-out infinite" }}
            />
          </svg>
        </div>
      </div>

      {/* Progress */}
      {showProgress && (
        <div className="w-full max-w-[200px] space-y-2">
          <div className="h-1.5 w-full rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
            <div
              className="h-full rounded-full bg-brand-500 transition-all duration-500 ease-out"
              style={{ width: `${clamped}%` }}
            />
          </div>
          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
            <span className="font-medium">{label}</span>
            <span className="tabular-nums">{clamped.toFixed(0)}%</span>
          </div>
        </div>
      )}

      {!showProgress && label && (
        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 animate-pulse">{label}</div>
      )}
    </div>
  );

  if (fullScreen) {
    return (
      <div
        className="fixed inset-0 w-full h-screen flex flex-col items-center justify-center bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm"
        style={{ zIndex: 100000 }}
      >
        {loaderContent}
      </div>
    );
  }

  return loaderContent;
}
